import { API_BASE_URL_ERA } from '../utils/config';
import customAxios from '../utils/axiosConfig';
// Removed mock imports - using real API calls only

// Helper function for API requests using customAxios
const makeRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL_ERA}/document-parser${endpoint}`;
  
  const config = {
    url,
    method: options.method || 'GET',
    ...options,
  };
  
  try {
    const response = await customAxios(config);
    return response.data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// Helper function to convert JSON Schema to frontend field format
export const convertJsonSchemaToFields = (jsonSchema) => {
  if (!jsonSchema || !jsonSchema.properties) {
    return [];
  }

  const convertProperty = (key, property, parentId = '') => {
    const fieldId = parentId ? `${parentId}-${key}` : `field-${key}`;
    
    let fieldType = 'string';
    switch (property.type) {
      case 'number':
      case 'integer':
        fieldType = 'number';
        break;
      case 'boolean':
        fieldType = 'boolean';
        break;
      case 'array':
        fieldType = 'array';
        break;
      case 'object':
        fieldType = 'object';
        break;
      case 'string':
        if (property.format === 'date' || property.format === 'date-time') {
          fieldType = 'date';
        } else {
          fieldType = 'string';
        }
        break;
      default:
        fieldType = 'string';
    }

    const field = {
      id: fieldId,
      name: key,
      type: fieldType,
      required: (jsonSchema.required && jsonSchema.required.includes(key)) || 
                (property.required && property.required.includes(key)) || false,
      description: property.description || '',
      defaultValue: property.default || '',
      validation: {},
      metadata: {
        placeholder: property.examples ? property.examples[0] : '',
        helpText: property.description || '',
        order: 0
      }
    };

    // Add validation rules
    if (property.pattern) {
      field.validation.pattern = property.pattern;
    }
    if (property.minimum !== undefined) {
      field.validation.min = property.minimum;
    }
    if (property.maximum !== undefined) {
      field.validation.max = property.maximum;
    }
    if (property.enum) {
      field.validation.enum = property.enum;
    }

    // Handle nested objects
    if (fieldType === 'object' && property.properties) {
      field.properties = {};
      Object.keys(property.properties).forEach(nestedKey => {
        const nestedField = convertProperty(nestedKey, property.properties[nestedKey], fieldId);
        field.properties[nestedKey] = nestedField;
      });
    }

    // Handle arrays
    if (fieldType === 'array' && property.items) {
      field.items = convertProperty('items', property.items, fieldId);
    }

    return field;
  };

  const fields = [];
  Object.keys(jsonSchema.properties).forEach(key => {
    fields.push(convertProperty(key, jsonSchema.properties[key]));
  });

  return fields;
};

// API functions with new route prefixes
export const documentParserAPI = {
  // Projects CRUD
  async getAllProjects() {
    try {
      // 優化：只載入基本項目列表，不包含Schema資訊
      const response = await makeRequest('/projects');
      const responseData = response.data || response;
      
      let transformedData = [];
      
      // 處理基本項目列表格式
      if (Array.isArray(responseData)) {
        transformedData = responseData.map(project => ({
          document_project_id: project.document_project_id,
          project_name: project.project_name,
          project_description: project.project_description,
          project_status: project.project_status,
          user_email: project.user_email,
          processed_files_count: project.processed_files_count || 0,
          error_files_count: project.error_files_count || 0,
          lastUpdated: project.updated_timestamp,
          createdAt: project.created_timestamp,
          config: project.project_config || {},
          schema_generation_collections: project.schema_generation_collections || [],
          document_parsing_collections: project.document_parsing_collections || []
        }));
      }
            
      return {
        data: transformedData,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw error;
    }
  },

  async getProjectById(project_id) {
    try {
      // 嘗試使用新的關聯查詢 API
      const response = await makeRequest(`/projects/${project_id}/with-schema`);
      const responseData = response.data || response;
      
      // 新 API 返回的格式：{ project: {...}, schema: {...}, has_schema: boolean }
      const projectData = responseData.project || responseData;
      
      // 轉換後端數據格式為前端期望格式
      const transformedData = {
        document_project_id: projectData.document_project_id,
        project_name: projectData.project_name,
        project_description: projectData.project_description,
        project_status: projectData.project_status,
        processed_files_count: projectData.processed_files_count || 0,
        error_files_count: projectData.error_files_count || 0,
        lastUpdated: projectData.updated_timestamp,
        createdAt: projectData.created_timestamp,
        config: projectData.project_config || {},
        schema_generation_collections: projectData.schema_generation_collections || [],
        document_parsing_collections: projectData.document_parsing_collections || []
      };
      
      // 如果有關聯的 schema，將其添加到 config 中
      if (responseData.has_schema && responseData.schema) {
        // 後端返回的是 document_schema_id，不是 schema_id
        transformedData.config.schema = responseData.schema.schema_definition;
        transformedData.config.schema_id = responseData.schema.document_schema_id;
        transformedData.config.schema_metadata = {
          schema_name: responseData.schema.schema_name,
          schema_description: responseData.schema.schema_description,
          created_timestamp: responseData.schema.created_timestamp,
          updated_timestamp: responseData.schema.updated_timestamp
        };

        // 同時設置 project_config 以便前端多個地方能找到 schema
        if (!transformedData.project_config) {
          transformedData.project_config = {};
        }
        transformedData.project_config.schema = responseData.schema.schema_definition;
        transformedData.project_config.schema_id = responseData.schema.document_schema_id;
      }
      
      return {
        data: transformedData,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch project ${project_id} with schema, trying fallback:`, error);

      // 如果新 API 失敗，嘗試使用舊的 API
      try {
        const response = await makeRequest(`/projects/${project_id}`);
        const rawData = response.data || response;
        const projectData = rawData.project || rawData;

        // 保持 project_config 原樣，這樣 schema 資訊會被保留
        const projectConfig = projectData.project_config || {};

        const transformedData = {
          document_project_id: projectData.document_project_id,
          project_name: projectData.project_name,
          project_description: projectData.project_description,
          project_status: projectData.project_status,
          processed_files_count: projectData.processed_files_count || 0,
          error_files_count: projectData.error_files_count || 0,
          lastUpdated: projectData.updated_timestamp,
          createdAt: projectData.created_timestamp,
          config: projectConfig,
          project_config: projectConfig,  // 也設置 project_config 以便前端可以找到 schema
          schema_generation_collections: projectData.schema_generation_collections || [],
          document_parsing_collections: projectData.document_parsing_collections || []
        };

        return {
          data: transformedData,
          success: true,
        };
      } catch (fallbackError) {
        console.error(`Both new and fallback APIs failed for project ${project_id}:`, fallbackError);
        throw fallbackError;
      }
    }
  },

  async createProject(projectData) {
    try {
      // 轉換前端欄位名稱為後端期望的格式
      const transformedData = {
        project_name: projectData.name,
        project_config: projectData.config
      };
      
      const response = await makeRequest('/projects', {
        method: 'POST',
        data: transformedData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  },

  async updateProject(project_id, projectData) {
    try {
      const response = await makeRequest(`/projects/${project_id}`, {
        method: 'PUT',
        data: projectData,
      });
      
      // 確保更新後的資料格式與前端期望一致
      const responseData = response.data || response;
      
      // 轉換後端數據格式為前端期望格式，與 getAllProjects 保持一致
      const transformedData = {
        document_project_id: responseData.document_project_id || project_id,
        project_name: responseData.project_name || projectData.name,
        project_description: responseData.project_description || projectData.description,
        project_status: responseData.project_status || 'active',
        processed_files_count: responseData.processed_files_count || 0,
        error_files_count: responseData.error_files_count || 0,
        lastUpdated: responseData.updated_timestamp || new Date().toISOString(),
        createdAt: responseData.created_timestamp,
        config: responseData.project_config || projectData.config || {},
        schema_generation_collections: responseData.schema_generation_collections || [],
        document_parsing_collections: responseData.document_parsing_collections || []
      };
      
      return {
        data: transformedData,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to update project ${project_id}:`, error);
      throw error;
    }
  },

  async deleteProject(project_id, cascade = true) {
    try {
      const response = await makeRequest(`/projects/${project_id}?cascade=${cascade}`, {
        method: 'DELETE',
      });
      return {
        data: response.data || { id: project_id },
        success: true,
      };
    } catch (error) {
      console.error(`Failed to delete project ${project_id}:`, error);
      throw error;
    }
  },

  // Statistics
  async getStatistics() {
    try {
      const response = await makeRequest('/statistics');
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      throw error;
    }
  },

  // Monitoring
  async getMonitoringData(project_id) {
    try {
      const response = await makeRequest(`/projects/${project_id}/monitoring`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch monitoring for ${project_id}:`, error);
      throw error;
    }
  },

  // Project control
  async toggleProjectStatus(project_id, newStatus) {
    try {
      const response = await makeRequest(`/projects/${project_id}/status`, {
        method: 'PUT',
        data: {
          status: newStatus
        }
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to toggle project ${project_id} status:`, error);
      throw error;
    }
  },



  // Manual folder processing
  async processProjectFolder(project_id, options = {}) {
    try {
      const {
        reprocess_mode = 'unprocessed_only'
      } = options;

      const requestData = {
        reprocess_mode
      };

      const response = await makeRequest(`/projects/${project_id}/process-folder`, {
        method: 'POST',
        data: requestData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to process folder for project ${project_id}:`, error);
      throw error;
    }
  },

  // Schema validation
  async validateSchema(schema) {
    try {
      const response = await makeRequest('/schemas/validate', {
        method: 'POST',
        data: schema,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to validate schema:', error);
      throw error;
    }
  },

  // Test processing
  async testProcessing(project_id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await makeRequest(`/projects/${project_id}/test-processing`, {
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to test processing for ${project_id}:`, error);
      throw error;
    }
  },



  // Schema CRUD operations
  async getAllSchemas() {
    try {
      const response = await makeRequest('/schemas');
      const rawData = response.data || response;
      
      // 轉換後端數據格式為前端期望格式
      let transformedData = [];
      const schemasArray = rawData.schemas || rawData;
      
      if (Array.isArray(schemasArray)) {
        transformedData = schemasArray.map(schema => {
          // 如果 schema_definition 是 JSON Schema 格式，轉換為前端欄位格式
          let definition = {
            fields: [],
            metadata: {
              name: schema.schema_name,
              description: schema.schema_description
            }
          };

          if (schema.schema_definition) {
            if (schema.schema_definition.properties) {
              // 這是 JSON Schema 格式，需要轉換
              const convertedFields = convertJsonSchemaToFields(schema.schema_definition);
              definition.fields = convertedFields;
              definition.metadata = {
                name: schema.schema_definition.title || schema.schema_name,
                description: schema.schema_definition.description || schema.schema_description,
                globalInstructions: schema.schema_definition.metadata?.globalInstructions || '',
                version: '1.0.0',
                createdAt: schema.created_timestamp,
                updatedAt: schema.updated_timestamp
              };
            } else if (schema.schema_definition.fields) {
              // 這已經是前端格式
              definition = schema.schema_definition;
            }
          }

          return {
            id: schema.document_schema_id,
            name: schema.schema_name,
            description: schema.schema_description,
            user_email: schema.user_email,
            definition: definition,
            created_at: schema.created_timestamp,
            updated_at: schema.updated_timestamp,
            // 保留後端返回的使用統計
            used_by_projects_count: schema.used_by_projects_count || 0,
            used_by_projects: schema.used_by_projects || []
          };
        });
      }
      
      return {
        data: transformedData,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch schemas:', error);
      throw error;
    }
  },

  async getSchemaById(schema_id) {
    try {
      const response = await makeRequest(`/schemas/${schema_id}`);
      const rawData = response.data || response;
      
      // 轉換後端數據格式為前端期望格式
      let definition = {
        fields: [],
        metadata: {
          name: rawData.schema_name,
          description: rawData.schema_description
        }
      };

      if (rawData.schema_definition) {
        if (rawData.schema_definition.properties) {
          // 這是 JSON Schema 格式，需要轉換
          const convertedFields = convertJsonSchemaToFields(rawData.schema_definition);
          definition.fields = convertedFields;
          definition.metadata = {
            name: rawData.schema_definition.title || rawData.schema_name,
            description: rawData.schema_definition.description || rawData.schema_description,
            globalInstructions: rawData.schema_definition.metadata?.globalInstructions || '',
            version: '1.0.0',
            createdAt: rawData.created_timestamp,
            updatedAt: rawData.updated_timestamp
          };
        } else if (rawData.schema_definition.fields) {
          // 這已經是前端格式
          definition = rawData.schema_definition;
        }
      }

      const transformedData = {
        id: rawData.document_schema_id,
        name: rawData.schema_name,
        description: rawData.schema_description,
        definition: definition,
        created_at: rawData.created_timestamp,
        updated_at: rawData.updated_timestamp,
        // 保留後端返回的使用統計
        used_by_projects_count: rawData.used_by_projects_count || 0,
        used_by_projects: rawData.used_by_projects || []
      };
      
      return {
        data: transformedData,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch schema ${schema_id}:`, error);
      throw error;
    }
  },

  async createSchema(schemaData) {
    try {
      const response = await makeRequest('/schemas', {
        method: 'POST',
        data: schemaData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to create schema:', error);
      throw error;
    }
  },

  async updateSchema(schema_id, schemaData) {
    try {
      const response = await makeRequest(`/schemas/${schema_id}`, {
        method: 'PUT',
        data: schemaData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to update schema ${schema_id}:`, error);
      throw error;
    }
  },

  async deleteSchema(schema_id) {
    try {
      const response = await makeRequest(`/schemas/${schema_id}`, {
        method: 'DELETE',
      });
      return {
        data: response.data || { id: schema_id },
        success: true,
      };
    } catch (error) {
      console.error(`Failed to delete schema ${schema_id}:`, error);
      throw error;
    }
  },

  // Schema statistics
  async getSchemaStatistics() {
    try {
      const response = await makeRequest('/schemas/statistics');
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch schema statistics:', error);
      throw error;
    }
  },

  // Ask API Integration for document parsing (ASYNC MODE) - NOW SUPPORTS DIRECT FILE UPLOAD
  async processDocumentWithAskAPI(project_id, requestData) {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add required fields
      formData.append('fileName', requestData.fileName);
      formData.append('schema', JSON.stringify(requestData.schema));
      
      // Add document processing configuration if provided
      if (requestData.documentProcessing) {
        formData.append('documentProcessing', JSON.stringify(requestData.documentProcessing));
      }
      
      // Add document parsing collections if provided
      if (requestData.documentParsingCollections) {
        formData.append('documentParsingCollections', JSON.stringify(requestData.documentParsingCollections));
      }
      
      // Handle different file types
      if (requestData.file) {
        // Direct file upload
        formData.append('file', requestData.file);
      } else if (requestData.fileId) {
        // Google Drive file
        formData.append('fileId', requestData.fileId);
      } else {
        throw new Error('Must provide either file or fileId');
      }
      
      const response = await makeRequest(`/projects/${project_id}/process-document`, {
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to start document processing for ${project_id}:`, error);
      
      // 重新拋出錯誤，讓前端組件處理
      throw new Error(`文件處理啟動失敗: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Get processing status using event_id
  async getProcessingStatus(event_id) {
    try {      
      const response = await makeRequest(`/processing-status/${event_id}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to get processing status for ${event_id}:`, error);
      throw new Error(`狀態查詢失敗: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Validate and get final result using event_id
  async validateProcessingResult(event_id, project_id, expected_schema, save_to_database = true, output_config = null, document_processing = null) {
    try {
      
      const requestData = {
        expected_schema,
        save_to_database
      };
      
      // 如果有傳入輸出配置，則包含在請求中
      if (output_config) {
        requestData.output_config = output_config;
      }
      
      // 如果有傳入文件處理配置，則包含在請求中
      if (document_processing) {
        requestData.document_processing = document_processing;
      }
      
      const response = await makeRequest(`/validate-result/${event_id}?project_id=${project_id}`, {
        method: 'POST',
        data: requestData,
      });
      
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to validate result for ${event_id}:`, error);
      throw new Error(`結果驗證失敗: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Generate schema from knowledge base
  async generateSchemaFromKnowledgeBase(knowledgeBaseId, documentSamples) {
    try {
      const response = await makeRequest('/ai/generate-schema-from-kb', {
        method: 'POST',
        data: { knowledgeBaseId, documentSamples },
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to generate schema from knowledge base:', error);
      throw error;
    }
  },

  // Get available knowledge bases
  async getAvailableKnowledgeBases() {
    try {
      const response = await makeRequest('/knowledge-bases');
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch knowledge bases:', error);
      throw error;
    }
  },

  // Generate schema with AI only (no knowledge base)
  async generateSchemaWithAI() {
    try {
      const response = await makeRequest('/ai/generate-schema', {
        method: 'POST',
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to generate schema with AI:', error);
      throw error;
    }
  },

  // Get available existing schemas
  async getAvailableSchemas() {
    try {
      const response = await makeRequest('/schemas/available');
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to fetch available schemas:', error);
      throw error;
    }
  },

  // Tasks API
  async getProjectTasks(project_id, params = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      
      // Add filter parameters
      if (params.status) queryParams.append('status', params.status);
      if (params.file_name) queryParams.append('file_name', params.file_name);
      
      // Add sorting parameters
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      const queryString = queryParams.toString();
      const url = `/projects/${project_id}/tasks${queryString ? `?${queryString}` : ''}`;
            
      const response = await makeRequest(url);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch tasks for project ${project_id}:`, error);
      throw error;
    }
  },

  async getTaskById(task_id) {
    try {
      const response = await makeRequest(`/tasks/${task_id}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch task ${task_id}:`, error);
      throw error;
    }
  },

  async getTaskDetail(project_id, task_id) {
    try {
      const response = await makeRequest(`/projects/${project_id}/tasks/${task_id}`);
      return {
        data: response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch task detail for ${task_id}:`, error);
      throw error;
    }
  },



  // AI Schema Generation Recovery
  async retryAIGeneration(projectId) {
    try {
      const response = await makeRequest(`/projects/${projectId}/retry-ai-generation`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Retry AI generation failed:', error);
      throw error;
    }
  },

  async resetToManual(projectId) {
    try {
      const response = await makeRequest(`/projects/${projectId}/reset-to-manual`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Reset to manual failed:', error);
      throw error;
    }
  },

  // Processing Status and Stage Details
  async getCeleryTaskStatus(taskId) {
    try {
      const response = await makeRequest(`/celery-task-status/${taskId}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to get celery task status ${taskId}:`, error);
      throw error;
    }
  },

  async getProjectProcessingStatus(projectId) {
    try {
      const response = await makeRequest(`/projects/${projectId}/processing-status`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to get project processing status ${projectId}:`, error);
      throw error;
    }
  },

  async terminateProjectProcessing(projectId) {
    try {
      const response = await makeRequest(`/projects/${projectId}/terminate-processing`, {
        method: 'POST',
      });
      return {
        data: response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to terminate processing for project ${projectId}:`, error);
      throw error;
    }
  },

  async deleteProjectTasks(projectId, statuses = null) {
    try {
      let url = `/projects/${projectId}/tasks`;
      if (statuses && statuses.length > 0) {
        const params = new URLSearchParams();
        statuses.forEach(status => params.append('status', status));
        url += `?${params.toString()}`;
      }
      
      const response = await makeRequest(url, {
        method: 'DELETE',
      });
      return {
        data: response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to delete tasks for project ${projectId}:`, error);
      throw error;
    }
  },

  async deleteSingleTask(projectId, taskId) {
    try {
      const response = await makeRequest(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'DELETE',
      });
      return {
        data: response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to delete task ${taskId}:`, error);
      throw error;
    }
  },

  // Schedule Management APIs
  async getProjectSchedules(projectId) {
    try {
      const response = await makeRequest(`/schedules/project/${projectId}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch schedules for project ${projectId}:`, error);
      throw error;
    }
  },

  async createSchedule(scheduleData) {
    try {
      const response = await makeRequest('/schedules/', {
        method: 'POST',
        data: scheduleData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error('Failed to create schedule:', error);
      throw error;
    }
  },

  async updateSchedule(scheduleId, scheduleData) {
    try {
      const response = await makeRequest(`/schedules/${scheduleId}`, {
        method: 'PUT',
        data: scheduleData,
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to update schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  async deleteSchedule(scheduleId) {
    try {
      const response = await makeRequest(`/schedules/${scheduleId}`, {
        method: 'DELETE',
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to delete schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  async getScheduleHistory(scheduleId, limit = 50) {
    try {
      const response = await makeRequest(`/schedules/${scheduleId}/history?limit=${limit}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch schedule history for ${scheduleId}:`, error);
      throw error;
    }
  },

  async executeScheduleManually(scheduleId) {
    try {
      const response = await makeRequest(`/schedules/${scheduleId}/execute`, {
        method: 'POST',
      });
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to manually execute schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  async getProjectExecutionHistory(projectId, limit = 50, offset = 0) {
    try {
      const response = await makeRequest(`/schedules/project/${projectId}/history?limit=${limit}&offset=${offset}`);
      return {
        data: response.data || response,
        success: true,
      };
    } catch (error) {
      console.error(`Failed to fetch execution history for project ${projectId}:`, error);
      throw error;
    }
  }
};

export default documentParserAPI;